import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './flaggedmessages.scss';
import withAuth from '../../auth/WithAuth';
import FurnitureNavbar from '../../../components/layout/Nav';
import { serverUrlAuth } from '../../../serverUrl';

const FlaggedMessages = () => {
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchFlaggedMessages();
  }, []);

  const fetchFlaggedMessages = async () => {
    try {
      setLoading(true);
      const response = await serverUrlAuth.get('/updates/admin/flagged-messages/');
      setFlaggedMessages(response.data);
    } catch (error) {
      console.error('Error fetching flagged messages:', error);
      toast.error('Failed to load flagged messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsReviewed = async (messageId, reviewMessage = '') => {
    try {
      setIsSubmitting(true);
      
      const response = await serverUrlAuth.post(
        `/updates/admin/flagged-messages/${messageId}/review/`,
        { review_message: reviewMessage }
      );

      // Update local state
      setFlaggedMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId ? { 
            ...msg, 
            reviewed: true,
            review_message: reviewMessage,
            reviewed_by: response.data.reviewed_by,
            reviewed_by_name: response.data.reviewed_by_name
          } : msg
        )
      );

      toast.success('Message marked as reviewed');
      
      // Reset review note and close modal
      setReviewNote('');
      if (selectedMessage?.id === messageId) {
        setIsModalOpen(false);
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      toast.error('Failed to mark as reviewed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFilteredMessages = () => {
    switch (filter) {
      case 'unreviewed':
        return flaggedMessages.filter((msg) => !msg.reviewed);
      case 'reviewed':
        return flaggedMessages.filter((msg) => msg.reviewed);
      default:
        return flaggedMessages;
    }
  };

  const getStatusBadge = (reviewed) => {
    return reviewed ? (
      <span className="status-badge reviewed">Reviewed</span>
    ) : (
      <span className="status-badge pending">Pending Review</span>
    );
  };

  const getMessageTypeBadge = (type) => {
    const typeConfig = {
      chat: { label: 'Chat', className: 'chat' },
      milestone: { label: 'Milestone', className: 'milestone' },
    };
    const config = typeConfig[type] || { label: type, className: 'default' };
    return (
      <span className={`type-badge ${config.className}`}>{config.label}</span>
    );
  };

  const openMessageDetail = (message) => {
    setSelectedMessage(message);
    setReviewNote(message.review_message || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
    setReviewNote('');
  };

  const handleMarkAsReviewed = () => {
    if (selectedMessage) {
      markAsReviewed(selectedMessage.id, reviewNote);
    }
  };

  const handleQuickReview = (messageId) => {
    markAsReviewed(messageId, 'Reviewed by admin');
  };

  const filteredMessages = getFilteredMessages();

  // Calculate statistics
  const totalFlagged = flaggedMessages.length;
  const pendingReview = flaggedMessages.filter(m => !m.reviewed).length;
  const reviewed = flaggedMessages.filter(m => m.reviewed).length;

  return (
    <>
      <FurnitureNavbar />
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="flagged-messages-admin">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-text">
              <h1>Flagged Messages Moderation</h1>
              <p>Review and manage messages flagged for containing contact information or links</p>
            </div>
            <div className="header-actions">
              <button 
                className="btn" 
                onClick={fetchFlaggedMessages} 
                disabled={loading}
              >
                {loading ? '↻ Refreshing...' : '↻ Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card total-flagged">
            <div className="stat-content">
              <div className="stat-icon">🚩</div>
              <div className="stat-info">
                <h3>Total Flagged</h3>
                <div className="stat-value">{totalFlagged}</div>
                <div className="stat-change neutral">
                  All flagged messages
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card pending-review">
            <div className="stat-content">
              <div className="stat-icon">⏳</div>
              <div className="stat-info">
                <h3>Pending Review</h3>
                <div className="stat-value">{pendingReview}</div>
                <div className="stat-change negative">
                  {pendingReview > 0 ? 'Requires attention' : 'All clear'}
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card reviewed">
            <div className="stat-content">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <h3>Reviewed</h3>
                <div className="stat-value">{reviewed}</div>
                <div className="stat-change positive">
                  {reviewed > 0 ? `${Math.round((reviewed / totalFlagged) * 100)}% complete` : 'Not started'}
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card all-messages">
            <div className="stat-content">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <h3>Currently Viewing</h3>
                <div className="stat-value">{filteredMessages.length}</div>
                <div className="stat-change neutral">
                  {filter === 'all' ? 'All messages' : 
                   filter === 'unreviewed' ? 'Pending only' : 
                   'Reviewed only'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-bar">
            <div className="filter-group">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All Messages
              </button>
              <button
                className={`filter-btn ${filter === 'unreviewed' ? 'active' : ''}`}
                onClick={() => setFilter('unreviewed')}
              >
                Pending Review
              </button>
              <button
                className={`filter-btn ${filter === 'reviewed' ? 'active' : ''}`}
                onClick={() => setFilter('reviewed')}
              >
                Reviewed
              </button>
            </div>

            <div className="filter-actions">
              <button 
                className="btn-secondary" 
                onClick={fetchFlaggedMessages} 
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </button>
              <div className="results-count">
                {filteredMessages.length} of {flaggedMessages.length} messages
              </div>
            </div>
          </div>
        </div>

        {/* Messages Section */}
        <div className="messages-section">
          <div className="section-header">
            <h3>Flagged Messages</h3>
            <div className="section-actions">
              <select className="export-btn">
                <option>Export Options</option>
                <option>Export as CSV</option>
                <option>Export as PDF</option>
              </select>
            </div>
          </div>

          <div className="messages-table-container">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading flagged messages...</p>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h3>No flagged messages found</h3>
                <p>
                  {filter === 'all'
                    ? 'There are no flagged messages to review.'
                    : filter === 'unreviewed'
                    ? 'All messages have been reviewed! Great job!'
                    : 'No messages have been marked as reviewed yet.'}
                </p>
                <button className="btn" onClick={fetchFlaggedMessages}>
                  Refresh Data
                </button>
              </div>
            ) : (
              <table className="flagged-messages-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Project</th>
                    <th>Sender</th>
                    <th>Message</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((message) => (
                    <tr
                      key={message.id}
                      className={!message.reviewed ? 'unreviewed' : 'reviewed'}
                      data-label-id={`message-${message.id}`}
                    >
                      <td className="message-id" data-label="ID">
                        #{message.id}
                      </td>
                      <td className="project-info" data-label="Project">
                        <div className="project-title">
                          {message.project_title}
                        </div>
                        <small>Project ID: {message.project}</small>
                      </td>
                      <td className="sender-info" data-label="Sender">
                        <div className="sender-name">{message.sender_name}</div>
                        <small>User ID: {message.sender}</small>
                      </td>
                      <td className="message-content" data-label="Message">
                        <div className="message-preview">
                          {message.message ? (
                            message.message.length > 100 ? (
                              `${message.message.substring(0, 100)}...`
                            ) : (
                              message.message
                            )
                          ) : (
                            <em>No message content</em>
                          )}
                        </div>
                        <button
                          className="view-details-btn"
                          onClick={() => openMessageDetail(message)}
                        >
                          View Details
                        </button>
                      </td>
                      <td data-label="Type">
                        {getMessageTypeBadge(message.type)}
                      </td>
                      <td data-label="Status">
                        {getStatusBadge(message.reviewed)}
                        {message.reviewed && message.reviewed_by_name && (
                          <div className="reviewed-by">
                            <small>by {message.reviewed_by_name}</small>
                          </div>
                        )}
                      </td>
                      <td className="date-cell" data-label="Date">
                        {new Date(message.created_at).toLocaleDateString()}
                        <small>
                          {new Date(message.created_at).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </small>
                      </td>
                      <td className="actions-cell" data-label="Actions">
                        {!message.reviewed ? (
                          <div className="action-buttons">
                            <button
                              className="mark-reviewed-btn quick"
                              onClick={() => handleQuickReview(message.id)}
                              title="Mark as reviewed without note"
                            >
                              Quick Review
                            </button>
                            <button
                              className="mark-reviewed-btn"
                              onClick={() => openMessageDetail(message)}
                            >
                              Review with Note
                            </button>
                          </div>
                        ) : (
                          <div className="reviewed-info">
                            <span className="already-reviewed">
                              ✓ Reviewed
                            </span>
                            {message.review_message && (
                              <div className="review-note-preview" title={message.review_message}>
                                <small>"{(message.review_message.length > 50) ? 
                                  `${message.review_message.substring(0, 50)}...` : 
                                  message.review_message}"</small>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Section Footer with Pagination */}
          {!loading && filteredMessages.length > 0 && (
            <div className="section-footer">
              <div className="footer-info">
                Showing {filteredMessages.length} messages
                {filter !== 'all' && ` (${filter === 'unreviewed' ? 'pending' : 'reviewed'} only)`}
              </div>
              <div className="pagination">
                <button className="page-btn" disabled>
                  ← Previous
                </button>
                <span className="page-info">Page 1 of 1</span>
                <button className="page-btn" disabled>
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Message Detail Modal */}
        {isModalOpen && selectedMessage && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Message Details</h2>
                <button className="close-modal" onClick={closeModal}>
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="message-header">
                  <div className="header-info">
                    <span className="message-id">
                      Message ID: #{selectedMessage.id}
                    </span>
                    {getMessageTypeBadge(selectedMessage.type)}
                    {getStatusBadge(selectedMessage.reviewed)}
                  </div>

                  <div className="project-details">
                    <h3>Project: {selectedMessage.project_title}</h3>
                    <p>Project ID: {selectedMessage.project}</p>
                  </div>

                  <div className="sender-details">
                    <h4>Sender Information</h4>
                    <p>Name: {selectedMessage.sender_name}</p>
                    <p>User ID: {selectedMessage.sender}</p>
                  </div>
                </div>

                <div className="message-body">
                  <h4>Message Content</h4>
                  <div className="message-text">
                    {selectedMessage.message || <em>No message content</em>}
                  </div>

                  {selectedMessage.note && (
                    <div className="additional-notes">
                      <h4>Additional Notes</h4>
                      <p>{selectedMessage.note}</p>
                    </div>
                  )}

                  {selectedMessage.progress_percentage > 0 && (
                    <div className="progress-info">
                      <h4>Progress Update</h4>
                      <div className="progress-bar-container">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${selectedMessage.progress_percentage}%`,
                            }}
                          ></div>
                        </div>
                        <span className="progress-percentage">
                          {selectedMessage.progress_percentage}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Review Section */}
                {!selectedMessage.reviewed ? (
                  <div className="review-section">
                    <h4>Add Review Note</h4>
                    <div className="review-note-input">
                      <textarea
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        placeholder="Add a note about your review (optional). Explain why this message was flagged and your decision."
                        rows={4}
                      />
                      <div className="note-helper">
                        <small>Optional: Add context for future reference</small>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="review-info-section">
                    <h4>Review Information</h4>
                    {selectedMessage.review_message ? (
                      <div className="review-message">
                        <strong>Review Note:</strong>
                        <p>{selectedMessage.review_message}</p>
                      </div>
                    ) : (
                      <p className="no-review-note">
                        <em>No review note provided</em>
                      </p>
                    )}
                    {selectedMessage.reviewed_by_name && (
                      <div className="reviewer-info">
                        <strong>Reviewed by:</strong>
                        <p>{selectedMessage.reviewed_by_name}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="message-meta">
                  <div className="meta-item">
                    <strong>Created:</strong>
                    <span>
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="meta-item">
                    <strong>Flagged:</strong>
                    <span
                      className={
                        selectedMessage.flagged ? 'flagged-yes' : 'flagged-no'
                      }
                    >
                      {selectedMessage.flagged ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="meta-item">
                    <strong>Reviewed:</strong>
                    <span
                      className={
                        selectedMessage.reviewed
                          ? 'reviewed-yes'
                          : 'reviewed-no'
                      }
                    >
                      {selectedMessage.reviewed ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {selectedMessage.type === 'milestone' && (
                    <div className="meta-item">
                      <strong>Type:</strong>
                      <span>Milestone Update</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                {!selectedMessage.reviewed ? (
                  <>
                    <button
                      className="review-action-btn secondary"
                      onClick={() => handleQuickReview(selectedMessage.id)}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Processing...' : 'Mark as Reviewed'}
                    </button>
                    <button
                      className="review-action-btn primary"
                      onClick={handleMarkAsReviewed}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Processing...' : '✓ Save with Note'}
                    </button>
                  </>
                ) : (
                  <button
                    className="review-action-btn secondary"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                )}
                <button className="close-btn" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default withAuth(FlaggedMessages, 'admin');